// ui/src/pages/AuctionResults.jsx
// Route: /auction/results

import { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { getRankImage } from '../common/consts';
import useStore from '../store/store';
import './AuctionResults.css';


const ROLE_COLORS = {
  duelist:    '#ff4655',
  initiator:  '#3ce2c2',
  controller: '#a78bfa',
  sentinel:   '#facc15',
  flex:       '#94a3b8',
};

// ── Icons ──────────────────────────────────────────────────────
const IconPeople    = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconPeopleOff = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="11" x2="23" y2="17"/><line x1="23" y1="11" x2="17" y2="17"/></svg>;
const IconTrend     = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
const IconCoins     = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/></svg>;
const IconTag       = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
const IconSearch    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IconChevron   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>;
const IconDownload  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const IconBarChart  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;

// ── Helpers ────────────────────────────────────────────────────
const initial  = (n) => (n || '?')[0].toUpperCase();
const safePct  = (spent, budget) => (!budget ? 0 : Math.min(100, Math.round((spent / budget) * 100)));
const barColor = (p) => p > 75 ? '#e24b4a' : p > 50 ? '#facc15' : '#3ce2c2';

// ── NEW SALE flash — shows at top of grid for 5s ───────────────
function NewSaleBanner({ player, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 5000);
    return () => clearTimeout(t);
  }, [onDone]);

  const roleColor = ROLE_COLORS[(player.role || '').toLowerCase()] || '#94a3b8';
  return (
    <div className="ar-new-sale">
      <span className="ar-new-sale-badge">NEW</span>
      <span className="ar-new-sale-name">{player.player_name}</span>
      <span className="ar-new-sale-meta" style={{ color: roleColor }}>{player.role}</span>
      <span className="ar-new-sale-price">{player.final_price} PTS</span>
      <span className="ar-new-sale-team">{player.sold_to}</span>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────
function AuctionResults() {
  const { currentSeason } = useStore();
  const [results,        setResults]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [activeTab,      setActiveTab]      = useState('sold');
  const [filterTeam,     setFilterTeam]     = useState('all');
  const [filterRole,     setFilterRole]     = useState('all');
  const [sortBy,         setSortBy]         = useState('price_desc');
  const [search,         setSearch]         = useState('');
  const [showComparison, setShowComparison] = useState(false);
  const [latestSale,     setLatestSale]     = useState(null);
  const prevSoldCount    = useRef(0);

  useEffect(() => {
    const load = async (id) => {
      try {
        const res = await apiService.getAuctionResults(id);
        if (res.data?.error) { setError('No auction session found.'); return; }
        setResults(res.data);
      } catch (err) {
        setError(err?.response?.data?.Message || 'Failed to load results');
      } finally {
        setLoading(false);
      }
    };

    if (!currentSeason?.id) {
      setLoading(false);
      return;
    }
    load(currentSeason.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSeason?.id]);

  // Detect new sale for flash banner
  useEffect(() => {
    if (!results?.sold_players) return;
    const sold = results.sold_players;
    if (prevSoldCount.current > 0 && sold.length > prevSoldCount.current) {
      // Most recent = highest index after sort by final_price desc
      const newest = [...sold].sort((a,b) => (b.final_price||0)-(a.final_price||0))[0];
      setLatestSale(newest);
    }
    prevSoldCount.current = sold.length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results?.sold_players?.length]);

  if (loading) return <div className="ar-loading"><div className="ar-spinner"/><p>Loading results…</p></div>;
  if (error || !results) return <div className="ar-error"><p>{error || 'No results available yet.'}</p></div>;

  const sold_players   = results?.sold_players  || [];
  const unsold_players = results?.unsold_players || [];
  const team_budgets   = results?.team_purse     || [];
  const summary        = results?.stats          || {};

  // ── Math ──────────────────────────────────────────────────
  const totalSold  = summary.total_sold         ?? sold_players.length;
  const totalUnsold= summary.total_unsold        ?? unsold_players.length;
  const totalSpent = summary.total_points_spent  ?? sold_players.reduce((s,p) => s+(p.final_price||0), 0);
  const highestBuy = summary.highest_buy         ?? (sold_players.length ? Math.max(...sold_players.map(p=>p.final_price||0)) : 0);
  const avgPrice   = totalSold > 0 ? Math.round(totalSpent / totalSold) : 0;

  // Top 3 for summary panel
  const top3 = [...sold_players]
    .sort((a,b) => (b.final_price||0)-(a.final_price||0))
    .slice(0, 3);

  // ── Filters ───────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const applySort = (arr) => {
    const c = [...arr];
    if (sortBy === 'price_desc') return c.sort((a,b) => (b.final_price||0)-(a.final_price||0));
    if (sortBy === 'price_asc')  return c.sort((a,b) => (a.final_price||0)-(b.final_price||0));
    if (sortBy === 'name_asc')   return c.sort((a,b) => (a.player_name||'').localeCompare(b.player_name||''));
    return c;
  };

  const filteredSold = applySort(sold_players.filter(p =>
    (filterTeam === 'all' || p.sold_to === filterTeam) &&
    (filterRole === 'all' || (p.role||'').toLowerCase() === filterRole) &&
    (!q || p.player_name?.toLowerCase().includes(q))
  ));

  const filteredUnsold = unsold_players.filter(p =>
    (filterRole === 'all' || (p.role||'').toLowerCase() === filterRole) &&
    (!q || p.player_name?.toLowerCase().includes(q))
  );

  const teamNames = team_budgets.map(t => t.team_name).filter(Boolean);
  const roles     = ['duelist','initiator','controller','sentinel','flex'];

  return (
    <div className="ar-root">

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="ar-header">
        <div>
          <div className="ar-eyebrow">VPL · SEASON 5</div>
          <h1 className="ar-title">AUCTION RESULTS</h1>
        </div>
        <button className="ar-export-btn" onClick={() => window.print()}>
          <IconDownload /> EXPORT REPORT
        </button>
      </div>

      {/* ── Stats + Top 3 sales ────────────────────────────── */}
      <div className="ar-hero">

        {/* Left: stat cards */}
        <div className="ar-stat-cards">
          <StatCard icon={<IconPeople />}    label="PLAYERS SOLD"   value={totalSold}           />
          <StatCard icon={<IconPeopleOff />} label="PLAYERS UNSOLD" value={totalUnsold}         />
          <StatCard icon={<IconTrend />}     label="HIGHEST SALE"   value={`${highestBuy} PTS`} teal highlight />
          <StatCard icon={<IconCoins />}     label="POINTS SPENT"   value={totalSpent}          />
          <StatCard icon={<IconTag />}       label="AVG SALE PRICE" value={`${avgPrice} PTS`}   />
        </div>

        {/* Top 3 sale cards */}
        <div className="ar-top3-cards">
          <div className="ar-top3-label">
            <span className="ts-trophy">🏆</span>
            <span className="ts-label">TOP AUCTION SALES</span>
          </div>
          <div className="ar-top3-grid">
            {top3.map((p, i) => (
              <TopSaleCard key={p.auction_player_id ?? i} player={p} rank={i} />
            ))}
          </div>
        </div>
      </div>

      {/* ── Team Purse ─────────────────────────────────────── */}
      <section className="ar-section">
        <div className="ar-section-hd">
          <h2 className="ar-section-title">TEAM PURSE</h2>
          <button className="ar-compare-btn" onClick={() => setShowComparison(true)}>
            <IconBarChart /> VIEW COMPARISON
          </button>
        </div>
        <div className="ar-purse-grid">
          {team_budgets.map((tb) => {
            const spent   = (tb.starting_budget||0) - (tb.remaining||0);
            const usedPct = safePct(spent, tb.starting_budget);
            const bc      = barColor(usedPct);
            const remLow  = (tb.remaining||0) < Math.round((tb.starting_budget||0) * 0.2);
            return (
              <div className="ar-purse-card" key={tb.team_name}>
                <div className="apc-head">
                  <div className="apc-logo">
                    {tb.team_logo
                      ? <img src={tb.team_logo} alt={tb.team_name} style={{ width:'100%', height:'100%', objectFit:'contain', padding:'4px' }}/>
                      : <span>{initial(tb.team_name)}</span>
                    }
                  </div>
                  <div className="apc-team-name">{tb.team_name}</div>
                </div>
                <div className="apc-stats">
                  <div className="apc-stat">
                    <div className="apc-stat-label">REMAINING</div>
                    <div className="apc-stat-val" style={{ color: remLow ? '#e24b4a' : '#facc15' }}>{tb.remaining} PTS</div>
                  </div>
                  <div className="apc-stat">
                    <div className="apc-stat-label">SPENT</div>
                    <div className="apc-stat-val">{spent} PTS</div>
                  </div>
                  <div className="apc-stat">
                    <div className="apc-stat-label">PLAYERS</div>
                    <div className="apc-stat-val">{tb.players_bought} / 6</div>
                  </div>
                </div>
                <div className="apc-bar-row">
                  <div className="apc-bar-bg">
                    <div className="apc-bar-fill" style={{ width:`${usedPct}%`, background: bc }}/>
                  </div>
                  <span className="apc-bar-pct" style={{ color: bc }}>{usedPct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Toolbar + Player grid ──────────────────────────── */}
      <div className="ar-toolbar">
        <div className="ar-toolbar-left">
          <div className="ar-search-wrap">
            <input className="ar-search" placeholder="Search player..." value={search} onChange={e=>setSearch(e.target.value)}/>
            <span className="ar-search-icon"><IconSearch/></span>
          </div>
          {activeTab === 'sold' && (
            <div className="ar-select-wrap">
              <select className="ar-select" value={filterTeam} onChange={e=>setFilterTeam(e.target.value)}>
                <option value="all">ALL TEAMS</option>
                {teamNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="ar-select-caret"><IconChevron/></span>
            </div>
          )}
          <div className="ar-select-wrap">
            <select className="ar-select" value={filterRole} onChange={e=>setFilterRole(e.target.value)}>
              <option value="all">ALL ROLES</option>
              {roles.map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}
            </select>
            <span className="ar-select-caret"><IconChevron/></span>
          </div>
          {activeTab === 'sold' && (
            <div className="ar-select-wrap">
              <select className="ar-select" value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                <option value="price_desc">PRICE: HIGH → LOW</option>
                <option value="price_asc">PRICE: LOW → HIGH</option>
                <option value="name_asc">NAME: A → Z</option>
              </select>
              <span className="ar-select-caret"><IconChevron/></span>
            </div>
          )}
        </div>
        <div className="ar-tabs">
          <button className={`ar-tab ${activeTab==='sold'?'active':''}`} onClick={()=>setActiveTab('sold')}>
            SOLD ({sold_players.length})
          </button>
          <button className={`ar-tab ${activeTab==='unsold'?'active':''}`} onClick={()=>setActiveTab('unsold')}>
            UNSOLD ({unsold_players.length})
          </button>
        </div>
      </div>

      <section className="ar-grid-section">
        {/* Latest sale flash */}
        {latestSale && (
          <NewSaleBanner player={latestSale} onDone={() => setLatestSale(null)}/>
        )}

        {activeTab === 'sold' && (
          <div className="ar-player-grid">
            {filteredSold.length === 0 && <p className="ar-empty">No players match the filter.</p>}
            {filteredSold.map((p,i) => <SoldCard key={p.auction_player_id ?? `sold-${i}`} player={p}/>)}
          </div>
        )}

        {activeTab === 'unsold' && (
          <div className="ar-player-grid">
            {filteredUnsold.length === 0 && <p className="ar-empty">No unsold players match the filter.</p>}
            {filteredUnsold.map((p,i) => <UnsoldCard key={p.auction_player_id ?? `unsold-${i}`} player={p}/>)}
          </div>
        )}
      </section>

      {/* ── Comparison modal ───────────────────────────────── */}
      {showComparison && (
        <div className="ar-modal-overlay" onClick={() => setShowComparison(false)}>
          <div className="ar-modal" onClick={e => e.stopPropagation()}>
            <div className="ar-modal-hd">
              <span className="ar-modal-title">TEAM BUDGET COMPARISON</span>
              <button className="ar-modal-close" onClick={() => setShowComparison(false)}>✕</button>
            </div>
            <div className="ar-modal-body">
              {team_budgets.map((tb) => {
                const spent   = (tb.starting_budget||0) - (tb.remaining||0);
                const usedPct = safePct(spent, tb.starting_budget);
                const bc      = barColor(usedPct);
                return (
                  <div className="cmp-row" key={tb.team_name}>
                    <div className="cmp-label">
                      <span className="cmp-team">{tb.team_name}</span>
                      <span className="cmp-players">{tb.players_bought}/6 players</span>
                    </div>
                    <div className="cmp-bars">
                      <div className="cmp-bar-wrap">
                        <div className="cmp-bar-label">Spent</div>
                        <div className="cmp-bar-bg"><div className="cmp-bar-fill" style={{ width:`${usedPct}%`, background: bc }}/></div>
                        <span className="cmp-bar-val" style={{ color: bc }}>{spent} pts</span>
                      </div>
                      <div className="cmp-bar-wrap">
                        <div className="cmp-bar-label">Remaining</div>
                        <div className="cmp-bar-bg"><div className="cmp-bar-fill" style={{ width:`${100-usedPct}%`, background:'#32F5D2' }}/></div>
                        <span className="cmp-bar-val" style={{ color:'#32F5D2' }}>{tb.remaining} pts</span>
                      </div>
                    </div>
                    <div className="cmp-pct" style={{ color: bc }}>{usedPct}%</div>
                  </div>
                );
              })}
              <div className="cmp-divider"/>
              <div className="cmp-players-section">
                {team_budgets.map((tb) => {
                  const tp = sold_players
                    .filter(p => p.sold_to === tb.team_name)
                    .sort((a,b) => (b.final_price||0)-(a.final_price||0));
                  if (!tp.length) return null;
                  return (
                    <div className="cmp-team-block" key={tb.team_name}>
                      <div className="cmp-team-hd">{tb.team_name}</div>
                      {tp.map((p,i) => (
                        <div className="cmp-player-row" key={p.auction_player_id ?? `cmp-${i}`}>
                          <span className="cmp-player-name">{p.player_name}</span>
                          <span className="cmp-player-role" style={{ color: ROLE_COLORS[(p.role||'').toLowerCase()]||'#94a3b8' }}>{p.role?.toUpperCase()}</span>
                          <span className="cmp-player-price">{p.final_price} pts</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────

function StatCard({ icon, label, value, teal, highlight }) {
  return (
    <div className={`ar-stat-card${highlight ? ' highlight' : ''}`}>
      <div className="sc-icon">{icon}</div>
      <div className="sc-text">
        <div className={`sc-val${teal ? ' teal' : ''}`}>{value}</div>
        <div className="sc-label">{label}</div>
      </div>
    </div>
  );
}

const MEDALS = ['🥇','🥈','🥉'];
const MEDAL_COLORS = ['#FFD700','#C0C0C0','#CD7F32'];

function TopSaleCard({ player, rank }) {
  const roleKey   = (player.role || '').toLowerCase();
  const roleColor = ROLE_COLORS[roleKey] || '#94a3b8';
  const rankImg   = getRankImage(player.peak_rank);
  const isFirst   = rank === 0;

  return (
    <div className={`tsc-card${isFirst ? ' tsc-card-first' : ''}`}>
      {/* Medal badge */}
      <div className="tsc-medal" style={{ color: MEDAL_COLORS[rank] }}>{MEDALS[rank]}</div>

      {/* Avatar */}
      <div className="tsc-avatar" style={{ borderColor: isFirst ? 'rgba(50,245,210,0.5)' : 'rgba(255,255,255,0.1)' }}>
        {player.photo
          ? <img src={player.photo} alt={player.player_name} loading="lazy"/>
          : <span>{initial(player.player_name)}</span>
        }
      </div>

      {/* Name + team */}
      <div className="tsc-name">{player.player_name}</div>
      <div className="tsc-team">{player.sold_to}</div>

      {/* Role + rank */}
      <div className="tsc-meta">
        <span className="tsc-role" style={{ color: roleColor }}>{player.role}</span>
        {rankImg && <img src={rankImg} alt={player.peak_rank} className="tsc-rank-img"/>}
        <span className="tsc-rank">{player.peak_rank}</span>
      </div>

      {/* Divider */}
      <div className="tsc-sep"/>

      {/* Price */}
      <div className="tsc-price-label">SOLD FOR</div>
      <div className={`tsc-price${isFirst ? ' tsc-price-first' : ''}`}>{player.final_price} PTS</div>
    </div>
  );
}

function SoldCard({ player }) {
  const roleKey   = (player.role || '').toLowerCase();
  const roleColor = ROLE_COLORS[roleKey] || '#94a3b8';

  return (
    <div className="apc-card" onClick={() => player.tracker && window.open(player.tracker,'_blank','noopener,noreferrer')}
         style={{ cursor: player.tracker ? 'pointer' : 'default' }}>
      <div className="apc-border"/>
      <div className="apc-avatar">
        {player.photo
          ? <img src={player.photo} alt={player.player_name} loading="lazy"/>
          : <span>{initial(player.player_name)}</span>
        }
      </div>
      <div className="apc-info">
        <div className="apc-name">{player.player_name}</div>
        <div className="apc-sub1">{player.sold_to}</div>
        <div className="apc-sub2">
          <span className="apc-role-dot" style={{ background: roleColor }}/>
          <span className="apc-role-text">{player.role}</span>
          <span className="apc-dot">·</span>
          <span className="apc-rank">{player.peak_rank}</span>
        </div>
      </div>
      <div className="apc-right">
        <div className="apc-price">{player.final_price} PTS</div>
        <div className="apc-sold">SOLD</div>
      </div>
    </div>
  );
}

function UnsoldCard({ player }) {
  const roleKey   = (player.role || '').toLowerCase();
  const roleColor = ROLE_COLORS[roleKey] || '#94a3b8';

  return (
    <div className="apc-card apc-card-unsold">
      <div className="apc-border" style={{ background: '#333' }}/>
      <div className="apc-avatar apc-avatar-dim">
        {player.photo
          ? <img src={player.photo} alt={player.player_name} loading="lazy"/>
          : <span>{initial(player.player_name)}</span>
        }
      </div>
      <div className="apc-info">
        <div className="apc-name">{player.player_name}</div>
        <div className="apc-sub2">
          <span className="apc-role-dot" style={{ background: roleColor }}/>
          <span className="apc-role-text">{player.role}</span>
          <span className="apc-dot">·</span>
          <span className="apc-rank">{player.peak_rank}</span>
        </div>
      </div>
      <div className="apc-right">
        <div className="apc-unsold-badge">UNSOLD</div>
      </div>
    </div>
  );
}

export default AuctionResults;